/**
 * Beachvision REST API client (JWT via global axios interceptors).
 */

import axios from 'axios';
import { BASE_URL } from '../utils/axios';

const prefix = '/api/v1';

/**
 * List current user's sessions (paginated).
 * @returns {Promise<{ results: object[], count?: number }>}
 */
export async function getSessions() {
  const { data } = await axios.get(`${BASE_URL}${prefix}/sessions/`);
  return data;
}

/**
 * Create a new coaching session.
 * @param {string} title
 * @param {string} recordedAt ISO datetime string
 */
export async function createSession(title, recordedAt) {
  const { data } = await axios.post(`${BASE_URL}${prefix}/sessions/`, {
    title,
    recorded_at: recordedAt,
  });
  return data;
}

/**
 * Poll session pipeline status.
 * @param {number|string} id
 */
export async function getSessionStatus(id) {
  const { data } = await axios.get(`${BASE_URL}${prefix}/sessions/${id}/status/`);
  return data;
}

/**
 * Re-enqueue the analysis workflow (server requires ≥10 min elapsed since upload/session start).
 * @param {number|string} sessionId
 */
export async function reprocessStuckSession(sessionId) {
  const { data } = await axios.post(`${BASE_URL}${prefix}/sessions/${sessionId}/reprocess/`);
  return data;
}

/**
 * Request presigned S3 PUT for direct browser upload.
 * @param {number|string} sessionId
 * @param {File} file
 */
export async function initDirectUpload(sessionId, file) {
  const contentType = (file.type || 'video/mp4').split(';')[0].trim().toLowerCase();
  const { data } = await axios.post(
    `${BASE_URL}${prefix}/sessions/${sessionId}/upload/init/`,
    {
      filename: file.name,
      content_type: contentType,
      file_size_bytes: file.size,
    }
  );
  return data;
}

/**
 * Confirm object landed in S3 and attach to session; starts analysis.
 * @param {number|string} sessionId
 * @param {string} storageKey
 */
export async function completeDirectUpload(sessionId, storageKey) {
  const { data } = await axios.post(
    `${BASE_URL}${prefix}/sessions/${sessionId}/upload/complete/`,
    { storage_key: storageKey }
  );
  return data;
}

/**
 * PUT file bytes to presigned S3 URL (XHR for upload progress).
 * @param {object} uploadSpec from init response: { url, headers }
 * @param {File} file
 * @param {(pct: number) => void} [onProgress]
 */
export function uploadFileToS3(uploadSpec, file, onProgress) {
  const { url, headers = {} } = uploadSpec;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    Object.entries(headers).forEach(([k, v]) => {
      if (v != null && v !== '') xhr.setRequestHeader(k, v);
    });
    xhr.upload.onprogress = (ev) => {
      if (!onProgress) return;
      const total =
        ev.lengthComputable && ev.total > 0 ? ev.total : file.size > 0 ? file.size : 0;
      if (total > 0) {
        onProgress(Math.min(100, Math.round((ev.loaded / total) * 100)));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(100);
        resolve();
        return;
      }
      const err = new Error(`S3 upload failed (${xhr.status})`);
      err.status = xhr.status;
      reject(err);
    };
    xhr.onerror = () => reject(new Error('S3 upload network error'));
    xhr.send(file);
  });
}

/**
 * Start analysis using selected bundled backend example video.
 * @param {number|string} sessionId
 * @param {string} [exampleVideoName]
 */
export async function uploadExampleVideo(sessionId, exampleVideoName) {
  const { data } = await axios.post(
    `${BASE_URL}${prefix}/sessions/${sessionId}/upload/example/`,
    exampleVideoName ? { example_video: exampleVideoName } : {}
  );
  return data;
}

/**
 * Fetch bundled example video frame as JPEG blob.
 * @param {string} [exampleVideoName]
 */
export async function getExampleVideoFrame(exampleVideoName) {
  const { data } = await axios.get(
    `${BASE_URL}${prefix}/example-video/frame/`,
    {
      params: exampleVideoName ? { name: exampleVideoName } : undefined,
      responseType: 'blob',
    }
  );
  return data;
}

/**
 * Full analysis (200) or throws axios error (202/503).
 * @param {number|string} id
 */
export async function getAnalysis(id) {
  const { data, status } = await axios.get(
    `${BASE_URL}${prefix}/analysis/${id}/`,
    { validateStatus: (s) => [200, 404].includes(s) }
  );
  if (status === 200) return { ok: true, data };
  const err = new Error(data?.error || 'Analysis request failed');
  err.response = { status, data };
  throw err;
}

/**
 * Signed frame URLs for points + takeaway-point clips.
 * @param {number|string} analysisId
 */
export async function getAnalysisFrames(analysisId) {
  const { data } = await axios.get(`${BASE_URL}${prefix}/analysis/${analysisId}/frames/`);
  return data;
}

/**
 * Point detail payload including signed clip URL.
 * @param {number|string} analysisId
 * @param {number|string} pointId
 */
export async function getAnalysisPoint(analysisId, pointId) {
  const { data } = await axios.get(`${BASE_URL}${prefix}/analysis/${analysisId}/points/${pointId}/`);
  return data;
}

/**
 * Takeaway detail payload including point-takeaway clips and drills.
 * @param {number|string} analysisId
 * @param {number|string} takeawayId
 */
export async function getAnalysisTakeawayDetail(analysisId, takeawayId) {
  const { data } = await axios.get(
    `${BASE_URL}${prefix}/analysis/${analysisId}/takeaways/${takeawayId}/`
  );
  return data;
}

/**
 * Session detail including nested analysis when present.
 * @param {number|string} id
 */
export async function getSession(id) {
  const { data } = await axios.get(`${BASE_URL}${prefix}/sessions/${id}/`);
  return data;
}

/**
 * Generate (or retrieve cached) an ffmpeg clip for a specific takeaway.
 * Returns { clip_url: string } with the absolute URL to the clipped video.
 * @param {number|string} sessionId
 * @param {number|string} takeawayId
 */
export async function getTakeawayClip(analysisId, takeawayId) {
  const { data } = await axios.get(
    `${BASE_URL}${prefix}/analysis/${analysisId}/takeaways/${takeawayId}/clip/`
  );
  return data;
}

/**
 * List all clips associated with a shared takeaway across points.
 * @param {number|string} sessionId
 * @param {number|string} takeawayId
 */
export async function getTakeawayClips(analysisId, takeawayId) {
  const { data } = await axios.get(
    `${BASE_URL}${prefix}/analysis/${analysisId}/takeaways/${takeawayId}/clips/`
  );
  return data;
}

/**
 * Return URL to a clipped point video.
 * @param {number|string} sessionId
 * @param {number|string} pointId
 */
export async function getPointClip(analysisId, pointId) {
  const { data } = await axios.get(
    `${BASE_URL}${prefix}/analysis/${analysisId}/points/${pointId}/clip/`
  );
  return data;
}

/**
 * Return URL to a point frame, optionally anchored to timestamp.
 * @param {number|string} analysisId
 * @param {number|string} pointId
 * @param {number|string|null|undefined} [timestampMs]
 */
export async function getPointFrame(analysisId, pointId, timestampMs) {
  const { data } = await axios.get(
    `${BASE_URL}${prefix}/analysis/${analysisId}/points/${pointId}/frame/`,
    timestampMs == null ? undefined : { params: { timestamp_ms: timestampMs } }
  );
  return data;
}

/**
 * Thumbs and optional re-analysis for a point (scoped to current analysis id in the URL).
 * @param {number|string} analysisId
 * @param {number|string} pointId
 * @param {object} body
 * @param {('thumbs_up'|'thumbs_down')} body.rating
 * @param {string[]} [body.reason_codes]
 * @param {string} [body.comment_text]
 * @param {boolean} [body.request_reanalysis]
 */
export async function postPointFeedback(analysisId, pointId, body) {
  const { data } = await axios.post(
    `${BASE_URL}${prefix}/analysis/${analysisId}/points/${pointId}/feedback/`,
    body
  );
  return data;
}

